import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { DialogUtility } from '@syncfusion/ej2-popups';
import { CookieService } from 'ngx-cookie-service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService } from '@core/services/authService/authentication.service';
import { InboxService } from '@core/services/inbox/inbox.service';
import { InboxListPaginationService } from '@modules/inbox/pages/inbox-list/inbox-list-pagination.service';
import { GlobalConstants } from '@shared/services/constants/globalconstants';
import { userActions } from 'src/app/store/state/user';

@Injectable({ providedIn: 'root' })
export class AuthCallbackResolver implements Resolve<boolean> {
  private readonly authProvidersList = ['hq', 'mineral'];
  private dialogObj: any;
  private isUrgent = false;
  private companyId: string | null = null;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly inboxService: InboxService,
    private readonly inboxListPaginationService: InboxListPaginationService,
    private readonly cookieService: CookieService,
    private readonly deviceService: DeviceDetectorService
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const code = route.queryParamMap.get('code') ?? '';
    const stateParam = route.queryParamMap.get('state') ?? '';

    if (!code) {
      return of(true);
    }

    if (
      stateParam &&
      stateParam.trim() !== '' &&
      !this.authProvidersList.some(provider => stateParam.startsWith(provider))
    ) {
      this.router.navigate(['/auth/login']);
      return of(false);
    }

    const callbackPath = state.url?.split('?')[0] ?? window.location.pathname;
    const navInfo = this.getNavInfoCookie();

    return this.authenticationService
      .FetchUserFromExternalAuthCode(callbackPath, code, stateParam, GlobalConstants.LoginType_CMS)
      .pipe(
        tap(user => {
          this.store.dispatch(userActions.userLoggedIn({ user }));
          this.companyId = user.CompanyID ? user.CompanyID.toString() : user.Company.CompanyID.toString();

          if (user.Company.RecognizeDeviceFlag && !this.checkCookieExist(user.UserEmail)) {
            this.dialogObj = DialogUtility.confirm({
              title: 'Save device Info',
              content: GlobalConstants.recogniseDeviceMessage,
              okButton: { text: 'Ok', click: this.saveDeviceInfo.bind(this, user.UserEmail) },
              cancelButton: { text: 'Cancel', cssClass: 'e-outline', click: this.cancelClick.bind(this) },
              showCloseIcon: false,
              closeOnEscape: false,
              animationSettings: { effect: 'Zoom' },
              zIndex: 1005,
              position: { X: 'center', Y: 'center' },
              isModal: true
            });
          }
        }),
        switchMap(user =>
          this.getInboxMessagesDialogContent().pipe(
            tap(messageContent => {
              if (!messageContent) {
                return;
              }

              const dialogOptions = {
                title: 'Inbox Message(s)',
                content: messageContent,
                okButton: { text: 'Go To Inbox', click: this.goToInboxPage.bind(this) },
                showCloseIcon: false,
                closeOnEscape: false,
                zIndex: 1005,
                position: { X: 'center', Y: 'center' },
                isModal: true
              };

              if (this.isUrgent) {
                this.dialogObj = DialogUtility.alert(dialogOptions);
              } else {
                this.dialogObj = DialogUtility.confirm({
                  ...dialogOptions,
                  cancelButton: { text: 'Cancel', click: this.cancelClick.bind(this), cssClass: 'e-outline' }
                });
              }
            }),
            map(() => user)
          )
        ),
        tap(user => {
          localStorage.setItem('currentSelectedNode', 'Dashboard');
          localStorage.setItem('emulatedUserStatus', JSON.stringify('false'));

          if (navInfo) {
            this.authenticationService.navigateAuthorizedUsers(
              navInfo.navigateTo,
              navInfo.navigateToUser,
              user
            );
            return;
          }

          if (user.UserAccessPrivilege === 3) {
            this.router.navigate(['/userlist'], { replaceUrl: true });
          } else {
            this.router.navigate(['/incidentreport'], { replaceUrl: true });
          }
        }),
        map(() => true),
        catchError(error => {
          console.log('Exception: ', error);
          this.router.navigate(['/auth/login']);
          return of(false);
        })
      );
  }

  private setCookie(userName: string): void {
    const deviceInfo = this.deviceService.getDeviceInfo();
    const userDeviceInfo = { userName, os: deviceInfo.os, browser: deviceInfo.browser };
    const expiresOn: Date = new Date();
    expiresOn.setMinutes(expiresOn.getMinutes() + GlobalConstants.DeviceTimeoutMinutes);
    this.cookieService.set(
      'userDevice',
      JSON.stringify(userDeviceInfo),
      expiresOn,
      '/',
      '',
      true,
      'None'
    );
  }

  private checkCookieExist(username: string): boolean {
    const cookie = this.cookieService.get('userDevice');
    const deviceInfo = this.deviceService.getDeviceInfo();

    if (!cookie) {
      return false;
    }

    const parsedCookie = JSON.parse(cookie);
    return (
      parsedCookie.userName === username &&
      parsedCookie.os === deviceInfo.os &&
      parsedCookie.browser === deviceInfo.browser
    );
  }

  private getNavInfoCookie(): any {
    const cookie = this.cookieService.get('navInfo');
    if (cookie && cookie !== '') {
      this.cookieService.delete('navInfo', '/');
      return JSON.parse(cookie);
    }
    return null;
  }

  private saveDeviceInfo(userName: string): void {
    this.dialogObj.hide();
    this.setCookie(userName);
  }

  private cancelClick(): void {
    this.dialogObj.hide();
  }

  private goToInboxPage(): void {
    this.dialogObj.hide();
    localStorage.setItem('currentSelectedNode', 'INBOX');
    this.router.navigate(['/inbox']);
    const state = { skip: 0, take: 25 };
    const messageIds: any[] = [];

    if (this.isUrgent) {
      this.inboxService.updateInboxMessages(messageIds, false).subscribe();
    }

    this.inboxListPaginationService.execute(state, this.companyId);
  }

  private getInboxMessagesDialogContent(): Observable<string | null> {
    if (!this.companyId) {
      return of(null);
    }

    return this.inboxService.getInboxMessagesFullData('', this.companyId, true).pipe(
      map(resp => {
        const body = resp.body ?? [];
        let messageContent: string | null = null;
        let isUnread = false;
        this.isUrgent = false;

        if (body.findIndex(item => item.SystemMessage.UrgentFlag === false && item.ViewedFlag === false) >= 0) {
          isUnread = true;
        }

        const unreadUrgent = body.filter(
          item => item.SystemMessage.UrgentFlag === true && item.ViewedFlag === false
        );

        if (unreadUrgent.length > 0) {
          this.isUrgent = true;
          if (unreadUrgent.length === 1) {
            const urgent = unreadUrgent[0];
            messageContent =
              '<strong class="e-text e-danger">! Urgent Message</strong>' +
              '<br><div class="urgent-msg-box"><div class="urgent-msg-from">From : ' +
              urgent.SystemMessage.CreatedBy +
              '</div><div class="urgent-msg-content">' +
              urgent.SystemMessage.MessageText +
              '</div></div>';
          } else {
            messageContent = '<strong class="e-text e-danger">! Urgent Messages</strong>';
            unreadUrgent.forEach(element => {
              messageContent +=
                '<div class="urgent-msg-box"><span class="urgent-msg-from">From : ' +
                element.SystemMessage.CreatedBy +
                '</span><div class="urgent-msg-content">' +
                element.SystemMessage.MessageText +
                '</div></div>';
            });
          }
        }

        if (isUnread || this.isUrgent) {
          if (isUnread && !this.isUrgent) {
            messageContent = GlobalConstants.unreadInboxMessage;
          } else if (isUnread && this.isUrgent) {
            messageContent =
              (messageContent ?? '') + '</br>' + GlobalConstants.unreadInboxMessage;
          }
        }

        return messageContent;
      })
    );
  }
}
